import type { DashboardRange } from "@adpilot/shared";
import { create } from "zustand";
import {
  addReportingDays,
  DEFAULT_REPORTING_TIME_ZONE,
  type ReportingTimeZone,
  reportingDateInTimeZone,
} from "@/lib/reporting-time-zone";

export const REPORTING_PERIOD_PRESETS = ["yesterday", "7d", "14d", "30d"] as const;

export type ReportingPeriodPreset = (typeof REPORTING_PERIOD_PRESETS)[number];

export type ReportingPeriod =
  | { type: "preset"; value: ReportingPeriodPreset }
  | { type: "custom"; startDate: string; endDate: string };

export interface ResolvedReportingPeriod {
  startDate: string;
  endDate: string;
}

export function resolveReportingPeriod(
  period: ReportingPeriod,
  now = new Date(),
  timeZone: ReportingTimeZone = DEFAULT_REPORTING_TIME_ZONE,
): ResolvedReportingPeriod {
  if (period.type === "custom") {
    return { startDate: period.startDate, endDate: period.endDate };
  }

  const today = reportingDateInTimeZone(now, timeZone);
  const endDate = period.value === "yesterday" ? addReportingDays(today, -1) : today;
  const days =
    period.value === "yesterday" ? 1 : Number.parseInt(period.value.replace("d", ""), 10);

  return {
    startDate: addReportingDays(endDate, -(days - 1)),
    endDate,
  };
}

export function reportingPeriodSearchParams(
  period: ReportingPeriod,
  timeZone: ReportingTimeZone = DEFAULT_REPORTING_TIME_ZONE,
  now = new Date(),
): {
  range: DashboardRange;
  startDate?: string;
  endDate?: string;
} {
  const resolved = resolveReportingPeriod(period, now, timeZone);
  return {
    range: period.type === "preset" ? period.value : "30d",
    startDate: resolved.startDate,
    endDate: resolved.endDate,
  };
}

interface ReportingPeriodState {
  period: ReportingPeriod;
  setPeriod: (period: ReportingPeriod) => void;
}

export const useReportingPeriodStore = create<ReportingPeriodState>((set) => ({
  period: { type: "preset", value: "30d" },
  setPeriod: (period) => set({ period }),
}));
